import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import {
  IVoucherLimitActions,
  IVoucherLimitState
} from "@saleor/discounts/hooks/useVoucherLimitsData";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import useNotifier from "@saleor/hooks/useNotifier";
import {
  hasNoError,
  runVoucherValidation
} from "@saleor/utils/validators/voucher";
import React, { Dispatch, useState } from "react";
import { useIntl } from "react-intl";

import {
  DiscountDeliveryTypeEnum,
  DiscountPaymentMethodEnum,
  DiscountValueTypeEnum,
  VoucherClassificationEnum,
  VoucherDiscountType,
  VoucherTypeEnum
} from "../../../types/globalTypes";
import { RequirementsPicker } from "../../types";
import VoucherDates from "../VoucherDates";
import VoucherDeliveryType from "../VoucherDeliveryType";
import VoucherDescription from "../VoucherDescription";
import VoucherFreeShippingValue from "../VoucherFreeShippingValue";
import VoucherInfo from "../VoucherInfo";
import VoucherLimits from "../VoucherLimits";
// import VoucherPaymentType from "../VoucherPaymentType";
import VoucherRequirements from "../VoucherRequirements";
import VoucherTypes from "../VoucherTypes";
import VoucherValue from "../VoucherValue";
import VoucherVerticals from "../VoucherVerticals";
export interface FormData {
  isMinEligibleOnly: boolean;
  applyToNewCustomer: boolean;
  applyOncePerCustomer: boolean;
  applyOncePerOrder: boolean;
  code: string;
  deliveryType: DiscountDeliveryTypeEnum[];
  description: string;
  discountType: DiscountValueTypeEnum;
  isAppliedToAllCustomers: boolean;
  isAppliedOnEligibleItems: boolean;
  isHidden: boolean;
  tenantDiscountShare: number;
  smopDiscountShare: number;
  endDate: string;
  endTime: string;
  hasEndDate: boolean;
  hasMinTenants: boolean;
  hasUsageLimit: boolean;
  maxDiscountValue: number;
  minCheckoutItemsQuantity: string;
  minSpent: string;
  minTenants: number;
  paymentType: DiscountPaymentMethodEnum;
  requirementsPicker: RequirementsPicker;
  startDate: string;
  startTime: string;
  type: VoucherTypeEnum;
  usageLimit: string;
  value: number;
  verticalType: VoucherClassificationEnum;
  multiplier: number;
  bulkCreate: boolean;
}

export interface VoucherCreatePageProps {
  defaultCurrency: string;
  disabled: boolean;
  errors: DiscountErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onSubmit: (data: FormData) => void;
  voucherLimitState: IVoucherLimitState;
  voucherLimitDispatcher: Dispatch<IVoucherLimitActions>;
}

const VoucherCreatePage: React.FC<VoucherCreatePageProps> = ({
  defaultCurrency,
  disabled,
  errors,
  saveButtonBarState,
  onBack,
  onSubmit,
  voucherLimitState,
  voucherLimitDispatcher
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const initialForm: FormData = {
    applyOncePerCustomer: true,
    applyOncePerOrder: false,
    applyToNewCustomer: false,
    bulkCreate: false,
    code: "",
    deliveryType: [
      DiscountDeliveryTypeEnum.NEXT_DAY,
      DiscountDeliveryTypeEnum.IN_STORE,
      DiscountDeliveryTypeEnum.EXPRESS_DELIVERY,
      DiscountDeliveryTypeEnum.EXPRESS_LONG_DISTANCE_DELIVERY,
      DiscountDeliveryTypeEnum.NATIONWIDE_DELIVERY,
      DiscountDeliveryTypeEnum.SAME_DAY
    ],
    description: "",
    discountType: DiscountValueTypeEnum.FIXED,
    endDate: "",
    endTime: "",
    hasEndDate: false,
    hasMinTenants: false,
    hasUsageLimit: false,
    isAppliedOnEligibleItems: false,
    isAppliedToAllCustomers: false,
    isHidden: true,
    isMinEligibleOnly: false,
    maxDiscountValue: 0,
    minCheckoutItemsQuantity: "0",
    minSpent: "0",
    minTenants: 0,
    multiplier: 0,
    paymentType: DiscountPaymentMethodEnum.ALL,
    requirementsPicker: RequirementsPicker.NONE,
    smopDiscountShare: 100,
    startDate: "",
    startTime: "",
    tenantDiscountShare: 0,
    type: VoucherTypeEnum.ENTIRE_ORDER,
    usageLimit: "0",
    value: 0,
    verticalType: VoucherClassificationEnum.ALL
  };

  const initialValidatedForm = {
    code: null,
    deliveryType: null,
    description: null,
    distribution: null,
    endDate: null,
    maxDiscountValue: null,
    minCheckoutItemsQuantity: null,
    minSpent: null,
    smopDiscountShare: null,
    tenantDiscountShare: null,
    usageLimit: null,
    value: null
  };
  const [clientErrors, setErrorValdiation] = useState(initialValidatedForm);
  const handleSubmit = event => {
    const validate = runVoucherValidation(event, null, voucherLimitState);
    if (hasNoError(validate)) {
      setErrorValdiation(validate);
      onSubmit(event);
    } else {
      if (validate.distribution) {
        notify({
          status: "error",
          text: intl.formatMessage(
            {
              defaultMessage: "{message}"
            },
            {
              message: validate.distribution
            }
          )
        });
      }
      setErrorValdiation(validate);
    }
  };

  const breadcrumbDirectory = [
    {
      name: "Vouchers",
      url: "/discounts/vouchers"
    },
    {
      name: "Create Voucher"
    }
  ];

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <PageBreadcrumbNavigation items={breadcrumbDirectory} />
          <div
            style={{ display: "flex", flexDirection: "row", marginTop: "10px" }}
          >
            <PageBackNavigation onBack={onBack} />
            <PageHeader
              title={intl.formatMessage({
                defaultMessage: "Create Voucher",
                description: "page header"
              })}
            />
          </div>
          <Grid>
            <div>
              <VoucherInfo
                data={data}
                errors={errors}
                clientErrors={clientErrors}
                disabled={
                  disabled || voucherLimitState.isLimitToCustomerActivity
                }
                onChange={change}
                variant="create"
              />
              <CardSpacer />
              <VoucherTypes
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
              />
              {data.discountType.toString() !== "SHIPPING" ? (
                <>
                  <CardSpacer />
                  <VoucherValue
                    data={data}
                    disabled={disabled}
                    defaultCurrency={defaultCurrency}
                    errors={errors}
                    clientErrors={clientErrors}
                    onChange={change}
                    variant="create"
                  />
                </>
              ) : (
                <>
                  <CardSpacer />
                  <VoucherFreeShippingValue
                    data={data}
                    disabled={disabled}
                    onChange={change}
                  />
                </>
              )}
              <CardSpacer />
              <VoucherDescription
                data={data}
                errors={errors}
                clientErrors={clientErrors}
                disabled={disabled}
                onChange={change}
                variant="create"
              />
              <CardSpacer />
              <VoucherVerticals
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
              />
              {data.discountType !== VoucherDiscountType.E_VOUCHER && (
                <>
                  <CardSpacer />
                  <VoucherDeliveryType
                    data={data}
                    disabled={disabled}
                    errors={errors}
                    clientErrors={clientErrors}
                    onChange={change}
                  />
                  {/* <CardSpacer />
                    <VoucherPaymentType
                      data={data}
                      disabled={disabled}
                      errors={errors}
                      onChange={change}
                    /> */}
                </>
              )}
              <CardSpacer />
              <VoucherRequirements
                data={data}
                disabled={disabled || data.discountType === "E_VOUCHER"}
                defaultCurrency={defaultCurrency}
                errors={errors}
                clientErrors={clientErrors}
                onChange={change}
              />
              <CardSpacer />
              <VoucherLimits
                data={data}
                disabled={disabled}
                defaultCurrency={defaultCurrency}
                errors={errors}
                clientErrors={clientErrors}
                onChange={change}
                voucherLimitState={voucherLimitState}
                voucherLimitDispatcher={voucherLimitDispatcher}
              />
              <CardSpacer />
              <VoucherDates
                data={data}
                disabled={disabled}
                defaultCurrency={defaultCurrency}
                clientErrors={clientErrors}
                errors={errors}
                onChange={change}
              />
            </div>
          </Grid>
          <SaveButtonBar
            id="voucherCreate"
            disabled={disabled || !hasChanged}
            onCancel={onBack}
            onSave={submit}
            state={saveButtonBarState}
          />
        </Container>
      )}
    </Form>
  );
};
VoucherCreatePage.displayName = "VoucherCreatePage";
export default VoucherCreatePage;
